import React, { forwardRef } from 'react';
import { clsx } from 'clsx';
import type { BlockRendererComponentType } from '@unifyapps/carbon/no-code/components/RegistryProvider/types';
import type { Appearance, ContainerBlockStateType } from '@unifyapps/defs/blocks/Container/types';
import { default as BaseStack } from '@unifyapps/ui/_components/Stack';
import { getBlockKey } from '@unifyapps/carbon/no-code/utils/block';
import { getBlockPositionDataAttributes } from '@unifyapps/carbon/no-code/utils/getBlockPositionDataAttributes';

type ContainerVariantProps = {
  component: ContainerBlockStateType;
  BlockRenderer: BlockRendererComponentType;
  className?: string;
  attributesAndEventListeners?: Record<string, unknown>;
  style: React.CSSProperties | undefined;
};

// [1]
export const Full = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('w-full', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => (
          <BaseStack key={getBlockKey(blockId, index)}>
            <BlockRenderer
              blockId={blockId}
              dataAttributes={getBlockPositionDataAttributes(index)}
              parentBlockId={component.id}
            />
          </BaseStack>
        ))}
      </BaseStack>
    );
  },
);
Full.displayName = 'Full';

// [1,1]
const TwoEqualColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-2', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => (
          <BaseStack key={getBlockKey(blockId, index)}>
            <BlockRenderer
              blockId={blockId}
              dataAttributes={getBlockPositionDataAttributes(index)}
              parentBlockId={component.id}
            />
          </BaseStack>
        ))}
      </BaseStack>
    );
  },
);
TwoEqualColumns.displayName = 'TwoEqualColumns';

// [1,1,1]
const ThreeEqualColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-3', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => (
          <BaseStack key={getBlockKey(blockId, index)}>
            <BlockRenderer
              blockId={blockId}
              dataAttributes={getBlockPositionDataAttributes(index)}
              parentBlockId={component.id}
            />
          </BaseStack>
        ))}
      </BaseStack>
    );
  },
);
ThreeEqualColumns.displayName = 'ThreeEqualColumns';

// [1,1,1,1]
const FourEqualColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-4', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => (
          <BaseStack key={getBlockKey(blockId, index)}>
            <BlockRenderer
              blockId={blockId}
              dataAttributes={getBlockPositionDataAttributes(index)}
              parentBlockId={component.id}
            />
          </BaseStack>
        ))}
      </BaseStack>
    );
  },
);
FourEqualColumns.displayName = 'FourEqualColumns';

// [1,1,1,1,1]
const FiveEqualColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-5', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => (
          <BaseStack key={getBlockKey(blockId, index)}>
            <BlockRenderer
              blockId={blockId}
              dataAttributes={getBlockPositionDataAttributes(index)}
              parentBlockId={component.id}
            />
          </BaseStack>
        ))}
      </BaseStack>
    );
  },
);
FiveEqualColumns.displayName = 'FiveEqualColumns';

// [1,1,1,1,1,1]
const SixEqualColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-6', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => (
          <BaseStack key={getBlockKey(blockId, index)}>
            <BlockRenderer
              blockId={blockId}
              dataAttributes={getBlockPositionDataAttributes(index)}
              parentBlockId={component.id}
            />
          </BaseStack>
        ))}
      </BaseStack>
    );
  },
);
SixEqualColumns.displayName = 'SixEqualColumns';

// [1,2]
const OneTwoColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-3', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => {
          return (
            <BaseStack
              className={clsx({
                'col-span-1': index === 0,
                'col-span-2': index === 1,
              })}
              key={getBlockKey(blockId, index)}
            >
              <BlockRenderer
                blockId={blockId}
                dataAttributes={getBlockPositionDataAttributes(index)}
                parentBlockId={component.id}
              />
            </BaseStack>
          );
        })}
      </BaseStack>
    );
  },
);
OneTwoColumns.displayName = 'OneTwoColumns';

// [1,3]
const OneThreeColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-4', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => {
          return (
            <BaseStack
              className={clsx({
                'col-span-1': index === 0,
                'col-span-3': index === 1,
              })}
              key={getBlockKey(blockId, index)}
            >
              <BlockRenderer
                blockId={blockId}
                dataAttributes={getBlockPositionDataAttributes(index)}
                parentBlockId={component.id}
              />
            </BaseStack>
          );
        })}
      </BaseStack>
    );
  },
);
OneThreeColumns.displayName = 'OneThreeColumns';

// [2,1]
const TwoOneColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-3', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => {
          return (
            <BaseStack
              className={clsx({
                'col-span-1': index === 1,
                'col-span-2': index === 0,
              })}
              key={getBlockKey(blockId, index)}
            >
              <BlockRenderer
                blockId={blockId}
                dataAttributes={getBlockPositionDataAttributes(index)}
                parentBlockId={component.id}
              />
            </BaseStack>
          );
        })}
      </BaseStack>
    );
  },
);
TwoOneColumns.displayName = 'TwoOneColumns';

// [3,1]
const ThreeOneColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-4', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => {
          return (
            <BaseStack
              className={clsx({
                'col-span-3': index === 0,
                'col-span-1': index === 1,
              })}
              key={getBlockKey(blockId, index)}
            >
              <BlockRenderer
                blockId={blockId}
                dataAttributes={getBlockPositionDataAttributes(index)}
                parentBlockId={component.id}
              />
            </BaseStack>
          );
        })}
      </BaseStack>
    );
  },
);
ThreeOneColumns.displayName = 'ThreeOneColumns';

// [3,2]
const ThreeTwoColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-5', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => {
          return (
            <BaseStack
              className={clsx({
                'col-span-3': index === 0,
                'col-span-2': index === 1,
              })}
              key={getBlockKey(blockId, index)}
            >
              <BlockRenderer
                blockId={blockId}
                dataAttributes={getBlockPositionDataAttributes(index)}
                parentBlockId={component.id}
              />
            </BaseStack>
          );
        })}
      </BaseStack>
    );
  },
);
ThreeTwoColumns.displayName = 'ThreeTwoColumns';

// [2,3]
const TwoThreeColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-5', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => {
          return (
            <BaseStack
              className={clsx({
                'col-span-2': index === 0,
                'col-span-3': index === 1,
              })}
              key={getBlockKey(blockId, index)}
            >
              <BlockRenderer
                blockId={blockId}
                dataAttributes={getBlockPositionDataAttributes(index)}
                parentBlockId={component.id}
              />
            </BaseStack>
          );
        })}
      </BaseStack>
    );
  },
);
TwoThreeColumns.displayName = 'TwoThreeColumns';

// [1,1,2]
const OneOneTwoColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-4', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => {
          return (
            <BaseStack
              className={clsx({
                'col-span-1': index === 0 || index === 1,
                'col-span-2': index === 2,
              })}
              key={getBlockKey(blockId, index)}
            >
              <BlockRenderer
                blockId={blockId}
                dataAttributes={getBlockPositionDataAttributes(index)}
                parentBlockId={component.id}
              />
            </BaseStack>
          );
        })}
      </BaseStack>
    );
  },
);
OneOneTwoColumns.displayName = 'OneOneTwoColumns';

// [1,2,1]
const OneTwoOneColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-4', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => {
          return (
            <BaseStack
              className={clsx({
                'col-span-1': index === 0 || index === 2,
                'col-span-2': index === 1,
              })}
              key={getBlockKey(blockId, index)}
            >
              <BlockRenderer
                blockId={blockId}
                dataAttributes={getBlockPositionDataAttributes(index)}
                parentBlockId={component.id}
              />
            </BaseStack>
          );
        })}
      </BaseStack>
    );
  },
);
OneTwoOneColumns.displayName = 'OneTwoOneColumns';

// [2,1,1]
const TwoOneOneColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-4', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => {
          return (
            <BaseStack
              className={clsx({
                'col-span-1': index === 1 || index === 2,
                'col-span-2': index === 0,
              })}
              key={getBlockKey(blockId, index)}
            >
              <BlockRenderer
                blockId={blockId}
                dataAttributes={getBlockPositionDataAttributes(index)}
                parentBlockId={component.id}
              />
            </BaseStack>
          );
        })}
      </BaseStack>
    );
  },
);
TwoOneOneColumns.displayName = 'TwoOneOneColumns';

// [2,1,2]
const TwoOneTwoColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-5', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => {
          return (
            <BaseStack
              className={clsx({
                'col-span-1': index === 1,
                'col-span-2': index === 0 || index === 2,
              })}
              key={getBlockKey(blockId, index)}
            >
              <BlockRenderer
                blockId={blockId}
                dataAttributes={getBlockPositionDataAttributes(index)}
                parentBlockId={component.id}
              />
            </BaseStack>
          );
        })}
      </BaseStack>
    );
  },
);
TwoOneTwoColumns.displayName = 'TwoOneTwoColumns';

// [1,4]
const OneFourColumns = forwardRef<HTMLDivElement, ContainerVariantProps>(
  ({ component, BlockRenderer, className, attributesAndEventListeners, style }, ref) => {
    return (
      <BaseStack
        className={clsx('grid w-full grid-cols-5', className)}
        ref={ref}
        style={style}
        {...attributesAndEventListeners}
      >
        {component.content.blockIds?.map((blockId, index) => {
          return (
            <BaseStack
              className={clsx({
                'col-span-1': index === 0,
                'col-span-4': index === 1,
              })}
              key={getBlockKey(blockId, index)}
            >
              <BlockRenderer
                blockId={blockId}
                dataAttributes={getBlockPositionDataAttributes(index)}
                parentBlockId={component.id}
              />
            </BaseStack>
          );
        })}
      </BaseStack>
    );
  },
);
OneFourColumns.displayName = 'OneFourColumns';

export const ContainerVariants: Partial<
  Record<
    Appearance['variant'],
    React.ForwardRefExoticComponent<ContainerVariantProps & React.RefAttributes<HTMLDivElement>>
  >
> = {
  '1': Full,
  '1_1': TwoEqualColumns,
  '1_2': OneTwoColumns,
  '1_3': OneThreeColumns,
  '2_1': TwoOneColumns,
  '2_3': TwoThreeColumns,
  '3_1': ThreeOneColumns,
  '3_2': ThreeTwoColumns,
  '1_1_2': OneOneTwoColumns,
  '1_2_1': OneTwoOneColumns,
  '2_1_1': TwoOneOneColumns,
  '2_1_2': TwoOneTwoColumns,
  '1_1_1': ThreeEqualColumns,
  '1_1_1_1': FourEqualColumns,
  '1_1_1_1_1': FiveEqualColumns,
  '1_1_1_1_1_1': SixEqualColumns,
  '1_4': OneFourColumns,
};
